import { TFunc, useTranslation } from 'libs/i18n';
import { TableColumn } from 'libs/ui';
import { formatDecimalValue } from 'modules/account/orderModule/common/helpers';

import { OrdersListItem } from '../../types';

import { renderDate, renderDateTime } from './renders/date';
import { renderStatus } from './renders/status';

interface GetColumnsArgs {
  brokerTimezoneOffset: number;
}

export const getColumns = (
  t: TFunc,
  { brokerTimezoneOffset }: GetColumnsArgs
): TableColumn<OrdersListItem>[] => [
  {
    field: 'number',
    title: t('account.orders.columns.number'),
    maxWidth: 115,
    minWidth: 115,
  },
  {
    field: 'signedAt',
    title: t('account.orders.columns.signedAt'),
    maxWidth: 148,
    minWidth: 148,
    sortable: true,
    renderCell: (item, field) =>
      renderDateTime(item, field, brokerTimezoneOffset),
  },
  {
    field: 'operationType',
    title: t('account.orders.columns.operationType'),
    maxWidth: 148,
    minWidth: 148,
    renderValue: (item) => t(`account.order.types.${item.operationType}`),
  },
  {
    field: 'status',
    title: t('account.orders.columns.status'),
    minWidth: 180,
    sortable: true,
    renderCell: (item) => renderStatus(item),
  },
  {
    field: 'startDate',
    title: t('account.orders.columns.startDate'),
    maxWidth: 120,
    minWidth: 120,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'endDate',
    title: t('account.orders.columns.endDate'),
    maxWidth: 120,
    minWidth: 120,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'closedDate',
    title: t('account.orders.columns.closedDate'),
    maxWidth: 120,
    minWidth: 120,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'isin',
    title: t('account.orders.columns.isin'),
    maxWidth: 140,
    minWidth: 140,
  },
  {
    field: 'currency',
    title: t('account.orders.columns.currency'),
    maxWidth: 70,
    minWidth: 70,
  },
  {
    field: 'unitPrice',
    title: t('account.orders.columns.unitPrice'),
    maxWidth: 148,
    minWidth: 148,
    renderValue: (item) => formatDecimalValue(item.unitPrice).toString(),
  },
  {
    field: 'quantity',
    title: t('account.orders.columns.quantity'),
    maxWidth: 110,
    minWidth: 110,
  },
  {
    field: 'transactionAmount',
    title: t('account.orders.columns.transactionAmount'),
    maxWidth: 148,
    minWidth: 148,
    renderValue: (item) =>
      formatDecimalValue(item.transactionAmount).toString(),
  },
];

export const useColumns = ({ brokerTimezoneOffset }: GetColumnsArgs) => {
  const { t } = useTranslation();
  const columns = getColumns(t, { brokerTimezoneOffset });

  return columns;
};
